import React, { useEffect, useState } from "react";
import { X, Mail } from "lucide-react";
import { toast } from "sonner";
import usePulseLocale from "./i18n";
import { tokens } from "./theme";
import { pulseGet, pulsePost } from "./api";

/** One-time newsletter opt-in on first Pulse visit. The "seen" flag lives
 *  SERVER-SIDE on the user profile (never localStorage): dismiss/accept on
 *  one device silences it everywhere. Brevo failure → soft toast, flag NOT
 *  set (retries next visit), navigation never blocked. */
export default function NewsletterPrompt({ dm }) {
  const { t } = usePulseLocale();
  const T = tokens(dm);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let on = true;
    pulseGet("newsletter-prompt")
      .then((d) => { if (on && d.show) setShow(true); })
      .catch(() => {});
    return () => { on = false; };
  }, []);

  if (!show) return null;

  const dismiss = () => {
    setShow(false);
    pulsePost("newsletter-prompt/dismiss").catch(() => {});
  };

  const subscribe = async () => {
    setBusy(true);
    try {
      await pulsePost("newsletter-prompt/subscribe");
      toast.success(t("nl_success"));
      setShow(false);
    } catch {
      toast.error(t("nl_error"));
      setShow(false); // hidden for this session only — server flag NOT set
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="dialog" aria-label={t("nl_title")} data-testid="pulse-newsletter-prompt"
      className="fixed bottom-24 right-4 max-w-sm w-[calc(100%-2rem)] p-4 pulse-fade-up"
      style={{ background: T.surface, border: `1px solid ${T.border}`, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", zIndex: 10000 }}>
      <div className="flex items-start gap-3">
        <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: T.accent }} aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-semibold mb-1" style={{ color: T.text }}>{t("nl_title")}</p>
          <p className="text-xs leading-relaxed" style={{ color: T.muted }}>{t("nl_body")}</p>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={subscribe} disabled={busy} data-testid="newsletter-prompt-accept"
              className="text-xs font-bold px-3 py-1.5 disabled:opacity-50"
              style={{ background: T.brand, color: "#fff", borderRadius: 2 }}>
              {busy ? "…" : t("nl_accept")}
            </button>
            <button onClick={dismiss} data-testid="newsletter-prompt-dismiss"
              className="text-xs font-semibold px-3 py-1.5"
              style={{ color: T.muted, border: `1px solid ${T.border}`, borderRadius: 2 }}>
              {t("nl_dismiss")}
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Close" data-testid="newsletter-prompt-close"
          className="flex-shrink-0 p-0.5" style={{ color: T.muted }}>
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
